import React, { useState } from 'react';
import { LogViewer } from './LogViewer';
import { DownloadButton } from './DownloadButton';
import type { RenderProgress as RenderProgressType } from '../../types';

const STAGE_LABELS: Record<string, string> = {
  idle:              'Idle',
  preparing:         'Preparing Assets',
  'loading-template':'Loading Template',
  'building-timeline':'Building Timeline',
  'rendering-frames':'Rendering Frames',
  'encoding-video':  'Encoding Video',
  'exporting-mp4':   'Exporting MP4',
  completed:         'Complete',
  error:             'Error',
};

const STAGE_ORDER = [
  'preparing',
  'loading-template',
  'building-timeline',
  'rendering-frames',
  'encoding-video',
  'exporting-mp4',
  'completed',
];

interface RenderProgressBarProps {
  progress: RenderProgressType | null;
  logs: string[];
  isRendering: boolean;
  isComplete: boolean;
  hasError: boolean;
  onDownload: () => void;
  onReset: () => void;
}

export function RenderProgressBar({
  progress,
  logs,
  isRendering,
  isComplete,
  hasError,
  onDownload,
  onReset,
}: RenderProgressBarProps) {
  const [showLogs, setShowLogs] = useState(false);

  const stage = progress?.stage ?? 'idle';
  const percent = progress?.percent ?? 0;
  const message = progress?.message ?? '';
  const stageLabel = STAGE_LABELS[stage] ?? stage;

  const barColor = hasError ? 'bg-red-500' : isComplete ? 'bg-green-500' : 'bg-blue-500';

  return (
    <div className="flex flex-col w-full">
      {/* Log viewer — above the bar, collapses */}
      {showLogs && logs.length > 0 && (
        <div className="px-4 pb-2 border-t border-zinc-700 pt-2">
          <LogViewer logs={logs} maxHeight={140} />
        </div>
      )}

      {/* Main bar row */}
      <div className="flex items-center gap-3 px-4 h-10 border-t border-zinc-700">
        {/* Stage label */}
        <span className="text-xs text-zinc-400 font-mono shrink-0 w-36 truncate">
          {stageLabel}
          {message && stage !== 'idle' && (
            <span className="text-zinc-600 ml-1">— {message}</span>
          )}
        </span>

        {/* Progress bar */}
        <div className="flex-1 h-1.5 rounded-full bg-zinc-700 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor} ${
              isRendering && !isComplete ? 'animate-none' : ''
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>

        {/* Percent */}
        <span className="text-[10px] font-mono text-zinc-500 w-8 text-right shrink-0">
          {stage !== 'idle' ? `${percent}%` : ''}
        </span>

        {/* Actions */}
        {isComplete && (
          <DownloadButton onDownload={onDownload} onReset={onReset} />
        )}

        {hasError && (
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-red-400 hover:text-red-300 transition-colors shrink-0"
          >
            Clear
          </button>
        )}

        {/* Log toggle */}
        {logs.length > 0 && (
          <button
            type="button"
            onClick={() => setShowLogs((v) => !v)}
            className="text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors shrink-0"
            title={showLogs ? 'Hide logs' : 'Show logs'}
          >
            {showLogs ? '▼ logs' : '▲ logs'}
          </button>
        )}
      </div>
    </div>
  );
}
